// Small presentation helpers for the public "comment by agenda item" view.
// Mirrors the design prototype's avatar coloring, initials, and relative timestamps.

import type { PublicComment, PublicCommentConfig } from "./types";

// Field definition ids belong to the customer, not the meeting, so they collide as soon
// as two meetings mount on one page -- which the portal events list now does. Scope every
// generated element id to the meeting. Lives here, not in PublicCommentApp, so the hook
// can share it without importing the component that imports the hook.
export const fieldIdPrefix = (config: PublicCommentConfig): string =>
  `pc-${config.offlineEventId}-`;

/**
 * Total public contributions on an item: each visible (non-held) top-level comment
 * plus its replies. Held/pending comments are author-only and excluded. Used for both
 * the per-item count pill and the header total so they always agree.
 */
export function countPublicComments(comments: PublicComment[]): number {
  return comments
    .filter((c) => !c.pending)
    .reduce((sum, c) => sum + 1 + c.replies.length, 0);
}

const AV_COLORS = [
  "#0383e0",
  "#07AFAA",
  "#c0392b",
  "#7A5AE0",
  "#c25a08",
  "#2c7a4b",
  "#b8358a"
];

export function avatarColor(name: string): string {
  let h = 0;
  for (const ch of name || "") {
    h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  }
  return AV_COLORS[h % AV_COLORS.length];
}

export function initials(name: string): string {
  const parts = (name || "").trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
}

// Server sends UTC timestamps. Parse robustly whether or not a timezone marker is present.
function parseUtc(value: string): number {
  if (!value) return NaN;
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const ms = Date.parse(hasZone ? value : value + "Z");
  return ms;
}

export function relativeTime(value: string): string {
  const ms = parseUtc(value);
  if (isNaN(ms)) return "";
  const diff = Date.now() - ms;
  if (diff < 0) return "Just now";
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  return new Date(ms).toLocaleDateString();
}
